"use strict"

import React from "react"
import deepEqual from "deep-equal"

import { logToServer } from "../ErrorBoundary"
import { normalizeRunDetails, getObjectsList, loadRuns, updateObject } from "../Loaders"
import { withAppSettingsConfig } from "./AppSettingsConfigProvider"

const getExperimentOptions = (experiments) => {
  const expMap = {}
  experiments.forEach((e) => {
    const project = `[${e.project_netsuite_project_id}] ${e.project_name}`
    if (!(project in expMap)) {
      expMap[project] = {
        label: project,
        options: [],
      }
    }

    expMap[project].options.push({
      id: e.id,
      value: e.name,
      label: e.name,
      task: e.project_task_name,
      project: project,
      active: e.active && e.project_active && e.project_task_active,
      obj: e,
    })
  })
  return Object.values(expMap)
}
export const FermManagementConfigContext = React.createContext({})

export function withFermManagementConfig(Component) {
  return function (props) {
    return (
      <FermManagementConfigContext.Consumer>
        {(configData) => <Component {...props} {...configData} />}
      </FermManagementConfigContext.Consumer>
    )
  }
}

class FermManagementConfigProvider extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      allReactors: [],
      liveRuns: [],
      liveLoading: true,
      liveRunErr: null,
      filteredRuns: [],
      filterLoading: true,
      filterRunErr: null,
      bioCatStocks: [],
      permissions: [],
      ableToEditRunName: false,
      runNameEditableByDefault: true,
      canInoculateSeparately: false,
      autoDataExportEnabled: false,
      canUseSKBCParents: false,
      BIOCONTROL_URL: "",
      BIOCONTROL_NAME: "",
      IS_PROJECT_EXPERIMENT_SITE: false,
      siteRunMetadata: {},
      allSystems: [],
      externalRuns: [],
      externalSamples: [],
      externalParents: [],
      menuTypeOptions: [],
      addEditRunWarn: null,
      experiments: [],
    }

    this.getLiveRuns = this.getLiveRuns.bind(this)
    this.getFilteredRuns = this.getFilteredRuns.bind(this)
    this.getReactors = this.getReactors.bind(this)
    this.loadData = this.loadData.bind(this)
    this.getExperiments = this.getExperiments.bind(this)
    this.getStocks = this.getStocks.bind(this)
    this.getSystems = this.getSystems.bind(this)
    this.setExternalRuns = this.setExternalRuns.bind(this)
    this.setExternalSamples = this.setExternalSamples.bind(this)
    this.getRunMetaFields = this.getRunMetaFields.bind(this)
    this.setAddEditRunWarn = this.setAddEditRunWarn.bind(this)
    this.inoculateRun = this.inoculateRun.bind(this)
    this.updateRun = this.updateRun.bind(this)
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    // Getting variables/info from Django settings - from AppSettingsConfig
    const permissions = this.props?.config?.user?.permissions || []
    const runNameEditableByDefault = !this.props?.config?.settings?.RUN_NAME_AUTO_MANAGED // True/False regarding default behavior if application should manage naming the Run - User can toggle this
    const ableToEditRunName = this.props?.config?.settings?.CAN_CUSTOMIZE_RUN_NAME // True/False - Hard setting whether custom run naming is allowed AT ALL - User can NOT toggle this
    const canInoculateSeparately = this.props?.config?.settings?.ALLOW_SEPARATE_RUN_INOCULATE
    const autoDataExportEnabled = this.props?.config?.settings?.AUTO_DATAEXPORT_CREATION
    const canUseSKBCParents = this.props?.config?.settings?.CAN_USE_SKOKIE_BIOCONTROL_PARENTS || false
    const BIOCONTROL_URL = this.props?.config?.settings?.BIOCONTROL_URL
    const BIOCONTROL_NAME = this.props?.config?.settings?.BIOCONTROL_NAME
    const IS_PROJECT_EXPERIMENT_SITE = this.props?.config?.settings?.IS_PROJECT_EXPERIMENT_SITE
    if (
      !deepEqual(permissions, this.state.permissions) ||
      ableToEditRunName !== this.state.ableToEditRunName ||
      runNameEditableByDefault !== this.state.runNameEditableByDefault ||
      canInoculateSeparately !== this.state.canInoculateSeparately ||
      autoDataExportEnabled !== this.state.autoDataExportEnabled ||
      canUseSKBCParents !== this.state.canUseSKBCParents ||
      BIOCONTROL_URL !== this.state.BIOCONTROL_URL ||
      IS_PROJECT_EXPERIMENT_SITE !== this.state.IS_PROJECT_EXPERIMENT_SITE
    ) {
      this.setState({
        permissions: permissions,
        ableToEditRunName: ableToEditRunName,
        runNameEditableByDefault: runNameEditableByDefault,
        canInoculateSeparately: canInoculateSeparately,
        autoDataExportEnabled: autoDataExportEnabled,
        canUseSKBCParents: canUseSKBCParents,
        BIOCONTROL_URL: BIOCONTROL_URL,
        BIOCONTROL_NAME: BIOCONTROL_NAME,
        IS_PROJECT_EXPERIMENT_SITE: IS_PROJECT_EXPERIMENT_SITE,
      })
    }
  }

  getLiveRuns() {
    this.setState({ liveLoading: true, liveRunErr: null })
    getObjectsList("/warehouse/runs/live/")
      .then((runs) => {
        this.setState({
          liveRuns: normalizeRunDetails(runs),
          liveLoading: false,
          liveRunErr: null,
        })
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
        this.setState({
          liveRunErr: err,
          liveLoading: false,
        })
      })
  }

  getFilteredRuns(queryUrl) {
    this.setState({ filterLoading: true, filterRunErr: null })
    getObjectsList(queryUrl)
      .then((runs) => {
        this.setState({
          filterRuns: normalizeRunDetails(runs?.rows || []),
          filterLoading: false,
          filterRunErr: null,
        })
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
        this.setState({
          filterRunErr: err,
          filterLoading: false,
        })
      })
  }

  getReactors() {
    getObjectsList("/warehouse/sources/reactor/runs/")
      .then((reactors) => {
        this.setState({
          allReactors: reactors,
        })
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
        this.setState({
          reactorErr: err,
        })
      })
  }

  getStocks() {
    getObjectsList("/laboratory/inventory/inventory-item/")
      .then((stocks) => {
        stocks = stocks.filter((stock) => stock["sku"]["inventory_type"]["name"] === "Biocatalyst")
        this.setState({
          bioCatStocks: stocks,
        })
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
        this.setState({
          stockErr: err,
        })
      })
  }

  setExternalSamples(samples) {
    this.setState({
      externalSamples: [...samples],
    })
  }

  setExternalRuns(runs) {
    this.setState({
      externalRuns: [...runs],
    })
  }

  buildServerlessQuery(sanitizedSearchString, parentType) {
    const currentDate = new Date()
    currentDate.setFullYear(currentDate.getFullYear() - 1) // Limiting search results to matches from the last year relative to today
    const year = currentDate.getFullYear()
    const month = String(currentDate.getMonth() + 1).padStart(2, "0")
    const day = String(currentDate.getDate()).padStart(2, "0")

    const searchQueryConfig = {
      external_samples: {
        queryName: "BioControlSample",
        methodName: "connectorBioControlSamples",
        cutoffPrefix: `takenAfter: "${year}-${month}-${day}"`,
        returnValues: "{id description comment takenOn run{ id description devices{identifier}} }",
      },
      external_runs: {
        queryName: "BioControlRun",
        methodName: "connectorBioControlRuns",
        cutoffPrefix: `startedAfter: "${year}-${month}-${day}"`,
        returnValues: "{id description comment devices{identifier} }",
      },
    }
    const { queryName, methodName, cutoffPrefix, returnValues } = {
      ...searchQueryConfig[parentType],
    }

    const queryPrefix = `query ${queryName} { ${methodName}(`
    const querySuffix = `)${returnValues}}`

    return (
      queryPrefix + `site: Skokie, ${cutoffPrefix}, searchString: "${sanitizedSearchString}"` + querySuffix
    )
  }

  getSystems() {
    getObjectsList("/warehouse/sources/system/runs/")
      .then((systems) => {
        this.setState({
          allSystems: systems,
        })
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
        this.setState({
          systemErr: err,
        })
      })
  }

  getRunMetaFields() {
    getObjectsList("/warehouse/run-metafields/")
      .then((metadata) => {
        const info = {}
        metadata.forEach((f) => {
          info[f.field] = f
        })
        this.setState({
          siteRunMetadata: info,
        })
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
        this.setState({
          siteRunMetadata: {},
        })
      })
  }

  getExperiments() {
    getObjectsList("/warehouse/experiments/")
      .then((experiments) => {
        this.setState({
          experiments: getExperimentOptions(experiments),
        })
      })
      .catch((err) => {
        logToServer(err)
        console.error(err)
      })
  }

  loadData() {
    this.getLiveRuns()
    this.getReactors()
    this.getStocks()
    this.getSystems()
    this.getExperiments()
  }

  setAddEditRunWarn(err) {
    this.setState({
      addEditRunWarn: err,
    })
  }

  inoculateRun(newRunInfo, onErr, onSuccess) {
    loadRuns("POST", newRunInfo, true)
      .then((_) => {
        onSuccess && onSuccess()
      })
      .catch((err) => {
        console.error(err)
        let error
        err.text().then((err) => {
          try {
            error = JSON.parse(err)
          } catch (e) {}
          this.setAddEditRunWarn(error?.message ? error.message : `Couldn't inoculate run: ${err.statusText}`)
          logToServer(`Couldn't inoculate run: ${error?.message ? error.message : err.statusText}`)
          onErr()
        })
      })
  }

  updateRun(run, onErr, updRunInfo, parentInfo, onSuccess) {
    // parentInfo should ONLY be passed here when it's being set for the very first time
    const runData = parentInfo ? { ...updRunInfo, parents: parentInfo } : updRunInfo

    updateObject(`/warehouse/runs/${run.id}/update_run_metadata/`, runData)
      .then((_) => {
        onSuccess && onSuccess()
      })
      .catch((err) => {
        onErr()
        console.error(err)
        logToServer(err)
        this.setAddEditRunWarn(err)
      })
  }

  componentDidMount() {
    this.loadData()
    this.getRunMetaFields()
    getObjectsList("/warehouse/run-metafields/get_menu_types/")
      .then((d) => {
        this.setState({
          menuTypeOptions: d,
        })
      })
      .catch((err) => {
        console.error(err)
        logToServer(err)
        this.setState({
          menuTypeOptions: [],
        })
      })
  }

  render() {
    return (
      <FermManagementConfigContext.Provider
        value={{
          getRunMetaFields: this.getRunMetaFields,
          loadData: this.loadData,
          inoculateRun: this.inoculateRun,
          updateRun: this.updateRun,
          setAddEditRunWarn: this.setAddEditRunWarn,
          getFilteredRuns: this.getFilteredRuns,
          buildServerlessQuery: this.buildServerlessQuery,
          setExternalSamples: this.setExternalSamples,
          setExternalRuns: this.setExternalRuns,
          getExperimentOptions: getExperimentOptions,
          getExperiments: this.getExperiments,
          ...this.state,
        }}
      >
        {this.props.children}
      </FermManagementConfigContext.Provider>
    )
  }
}

export default withAppSettingsConfig(FermManagementConfigProvider)
